import { Button, Descriptions, Drawer, Flex, Popconfirm, message, Table, Tag, Typography } from 'antd';
import { useRequest } from 'ahooks';
import { useEffect, useState } from 'react';
import AccessButton from '@/components/AccessButton';
import { openScheduledReportModal } from './ScheduledReportModal';
import { analysisApi, ScheduledExecutionStatus, ScheduledReportPlan } from '@/axios/analysis';

const sources: Record<ScheduledReportPlan['dataSource'], string> = {
  COMPANY_REPORT: '客户报表',
  CLIENT_INDUSTRY_BRAND: '客户后台（行业品牌看板）',
  CLIENT_DATA_DASHBOARD: '客户后台（报表）',
};
const ranges: Record<ScheduledReportPlan['timeRange'], string> = {
  ALL: '全部', LAST_7_DAYS: '近7天', LAST_30_DAYS: '近30天', LATEST_1_DAY: '最新1天',
};
const statuses: Record<ScheduledExecutionStatus, [string, string]> = {
  WAITING: ['等待执行', 'default'], RUNNING: ['生成中', 'processing'], RETRY_WAIT: ['等待重试', 'warning'],
  SUCCESS: ['完成', 'success'], NO_DATA: ['无数据', 'default'], FAILED: ['失败', 'error'],
  INVALIDATED: ['失效', 'default'], SKIPPED_EXPIRED: ['企业到期，已跳过', 'default'],
};
const date = (value?: string) => value?.replace('T', ' ').replace(/\.\d+$/, '') || '—';
const status = (value?: ScheduledExecutionStatus) => value && statuses[value]
  ? <Tag color={statuses[value][1]}>{statuses[value][0]}</Tag> : '尚未执行';

export default function ScheduledReportTable({ companyId, refreshKey, onReport }: {
  companyId: string; refreshKey: number; onReport: (id: string) => void;
}) {
  const [page, setPage] = useState({ pageIndex: 1, pageSize: 10 });
  const [selected, setSelected] = useState<ScheduledReportPlan>();
  const [executionPage, setExecutionPage] = useState({ pageIndex: 1, pageSize: 10 });
  const plans = useRequest(() => analysisApi.scheduledPlans({ companyId, ...page }), {
    refreshDeps: [companyId, page, refreshKey], pollingInterval: 30000,
  });
  const detail = useRequest(analysisApi.scheduledDetail, { manual: true });
  const executions = useRequest(analysisApi.scheduledExecutions, { manual: true, pollingInterval: 10000 });
  useEffect(() => {
    if (selected) {
      void detail.runAsync(selected.planId).catch(() => {});
      void executions.runAsync({ planId: selected.planId, ...executionPage }).catch(() => {});
    }
    return () => executions.cancel();
  }, [selected, executionPage]);
  const plan = detail.data?.planId === selected?.planId ? detail.data : selected;
  return <>
    <Flex justify="space-between" align="center">
      <Typography.Title level={5} style={{ margin: 0 }}>定时任务</Typography.Title>
      <Button onClick={plans.refresh} loading={plans.loading}>刷新任务</Button>
    </Flex>
    <Table<ScheduledReportPlan> size="small" rowKey="planId" loading={plans.loading} dataSource={plans.data?.records || []}
      scroll={{ x: 1000 }} pagination={{ current: page.pageIndex, pageSize: page.pageSize, total: plans.data?.total,
        showSizeChanger: true, onChange: (pageIndex, pageSize) => setPage({ pageIndex, pageSize }) }}
      columns={[
        { title: '任务名称', dataIndex: 'taskName' },
        { title: '数据来源', dataIndex: 'dataSource', render: (value: ScheduledReportPlan['dataSource']) => sources[value] },
        { title: '时间范围', dataIndex: 'timeRange', render: (value: ScheduledReportPlan['timeRange']) => ranges[value] },
        { title: '执行周期', dataIndex: 'intervalDays', render: (value) => `每隔 ${value} 天` },
        { title: '计划状态', dataIndex: 'status', render: (value) => value === 'PAUSED' ? '已暂停' : value === 'CANCELED' ? '已取消' : value === 'EXPIRED' ? '企业已到期' : '运行中' },
        { title: '下次执行（北京时间）', dataIndex: 'nextRunTime', render: (value, row) => row.status === 'PAUSED' || row.status === 'CANCELED' ? '—' : row.status === 'EXPIRED' ? '续期后恢复未来周期' : date(value) },
        { title: '最近执行状态', dataIndex: 'latestStatus', render: status },
        { title: '操作', width: 230, render: (_, row) => {
          const paused = row.status === 'PAUSED';
          return <Flex gap={4}>
          {row.status !== 'CANCELED' && <>
            <AccessButton permission={['company:analysis:create']}>
            <Button type="link" disabled={row.latestStatus === 'RUNNING'} onClick={() => {
              void analysisApi.scheduledDetail(row.planId).then(plan => {
                openScheduledReportModal({ companyId, plan }, { title: '编辑定时分析报告' }, plans.refresh);
              }).catch(() => {});
            }}>编辑</Button>
            </AccessButton>
            <AccessButton permission={['company:analysis:create']}>
            <Popconfirm key={row.status} title={paused ? '确定恢复此定时任务？' : '确定暂停此定时任务？'}
              description={paused ? '将按原周期继续执行，不补跑暂停期间的任务。' : '将停止后续执行和重试，当前未完成的执行将失效；历史报告保留，可随时恢复。'}
              okText={paused ? '确定恢复' : '确定暂停'} cancelText="暂不操作" onConfirm={async () => {
                await (paused ? analysisApi.resumeScheduled : analysisApi.pauseScheduled)(row.planId);
                message.success(paused ? '定时任务已恢复' : '定时任务已暂停'); plans.refresh();
              }}>
              <Button type="link">{paused ? '恢复' : '暂停'}</Button>
            </Popconfirm>
            </AccessButton>
          </>}
          <AccessButton permission={['company:analysis:create']}>
          <Popconfirm title="确定删除此定时任务？" description="将删除任务和执行记录，已生成的历史报告保留；正在生成时无法删除。"
            okText="确定删除" cancelText="暂不删除" onConfirm={async () => {
              await analysisApi.deleteScheduled(row.planId);
              message.success('定时任务已删除');
              if (selected?.planId === row.planId) setSelected(undefined);
              plans.refresh();
            }}>
            <Button type="link" danger>删除</Button>
          </Popconfirm>
          </AccessButton>
          <Button type="link" onClick={() => {
            setExecutionPage({ pageIndex: 1, pageSize: 10 }); setSelected(row);
          }}>执行记录</Button>
        </Flex>;
        } },
      ]} />
    <Drawer title={selected?.taskName || '执行记录'} open={!!selected} width={1100} onClose={() => setSelected(undefined)}>
      {plan && <Descriptions bordered size="small" column={2} items={[
        { key: 'source', label: '数据来源', children: sources[plan.dataSource] },
        { key: 'range', label: '时间范围', children: ranges[plan.timeRange] },
        { key: 'first', label: '首次执行（北京时间）', children: date(plan.firstRunTime) },
        { key: 'interval', label: '执行周期', children: `每隔 ${plan.intervalDays} 天` },
        { key: 'packages', label: '词包', span: 2, children: plan.packages?.map(p => p.name).join('、') || '加载中' },
      ]} />}
      <Flex justify="space-between" align="center" style={{ margin: '20px 0' }}>
        <span>失败后每隔 5 分钟重试，最多重试 3 次。</span>
        <Button onClick={executions.refresh} loading={executions.loading}>刷新记录</Button>
      </Flex>
      <Table rowKey="executionId" loading={executions.loading} dataSource={executions.data?.records || []} size="small"
        scroll={{ x: 1000 }} pagination={{ current: executionPage.pageIndex, pageSize: executionPage.pageSize,
          total: executions.data?.total, onChange: (pageIndex, pageSize) => setExecutionPage({ pageIndex, pageSize }) }}
        columns={[
          { title: '应执行时间', dataIndex: 'dueTime', render: date },
          { title: '状态', dataIndex: 'status', render: status },
          { title: '重试次数', dataIndex: 'retryCount', render: (value) => `${value} / 3` },
          { title: '下次重试', dataIndex: 'nextRetryTime', render: date },
          { title: '开始 / 结束', render: (_, row) => <>{date(row.startedAt)}<br />{date(row.finishedAt)}</> },
          { title: '失败原因', dataIndex: 'failureMessage', render: (value) => value || '—' },
          { title: '报告', render: (_, row) => row.status === 'SUCCESS' && row.analysisId
            ? <Button type="link" onClick={() => onReport(row.analysisId!)}>查看报告</Button> : '—' },
        ]} />
    </Drawer>
  </>;
}
